import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ShieldAlert, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { PortalLayout } from "@/components/portal/PortalLayout";
import { Assessment } from "@/components/portal/Assessment";
import { TraitBar } from "@/components/portal/Primitives";
import { portalApi } from "@/lib/portalApi";
import { track } from "@/lib/analytics";
import {
  DARK_TRIAD_ITEMS, DARK_TRIAD_TRAITS, TRAIT_LABELS, DARK_TRIAD_PROFILES,
  calculateDarkTriadScores, getDominantDarkTriadTrait,
} from "@/data/questionnaires";

const SEVERITY_COLOR = (v) => (v >= 70 ? "#F87171" : v >= 45 ? "#FB923C" : "#4ADE80");

export default function DarkTriadPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [scores, setScores] = useState(null);

  const complete = async (responses) => {
    setSubmitting(true);
    const calculated = calculateDarkTriadScores(responses);
    try {
      await portalApi.saveDarkTriad(id, calculated);
      setScores(calculated);
      track("assessment_completed", { assessment: "leader_d3" });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (scores) {
    const dominant = getDominantDarkTriadTrait(scores);
    const profile = DARK_TRIAD_PROFILES[dominant];
    return (
      <PortalLayout>
        <div data-testid="dark-triad-results">
          <div className="mb-10">
            <ShieldAlert className="text-[#D4AF37] mb-5" size={34} />
            <p className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] mb-3">Leader D3 profile</p>
            <h1 className="font-serif text-3xl md:text-4xl text-white mb-3" data-testid="dark-triad-dominant">
              {profile.name}
            </h1>
            <p className="text-slate-400 text-sm max-w-2xl leading-relaxed">{profile.description}</p>
          </div>

          <div className="border border-slate-800 bg-[#0B1220]/80 p-6 md:p-8 space-y-6 mb-8">
            {DARK_TRIAD_TRAITS.map((trait) => (
              <TraitBar
                key={trait}
                label={TRAIT_LABELS[trait]}
                value={scores[trait]}
                color={SEVERITY_COLOR(scores[trait])}
                testid={`dark-triad-score-${trait}`}
              />
            ))}
          </div>

          <div className="border border-slate-800 bg-[#0B1220]/80 p-6 md:p-8 mb-9">
            <h2 className="font-serif text-xl text-white mb-5">How to handle this pattern</h2>
            <ul className="space-y-3">
              {profile.strategies.map((s) => (
                <li key={s} className="flex gap-3 text-slate-300 text-sm leading-relaxed">
                  <span className="text-[#D4AF37] mt-0.5">—</span>
                  {s}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => navigate(`/portal/incident/${id}`)}
              data-testid="dark-triad-to-incident-btn"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-slate-950 px-7 py-3.5 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#B5952F] transition-colors"
            >
              Back to advisor <ArrowRight size={14} />
            </button>
            <button
              onClick={() => navigate("/portal/report")}
              data-testid="dark-triad-to-report-btn"
              className="inline-flex items-center gap-2 border border-slate-700 text-slate-300 px-7 py-3.5 text-xs uppercase tracking-[0.2em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            >
              Build vulnerability report
            </button>
          </div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout back={`/portal/incident/${id}`}>
      <Assessment
        items={DARK_TRIAD_ITEMS}
        eyebrow="Assessment 2 of 2"
        title="Leader D3 assessment"
        intro="Rate your leader on 15 behaviours you can actually observe at work. This places them on the three dark triad dimensions — narcissism, Machiavellianism and psychopathy. It is a behavioural read, never a clinical diagnosis."
        submitting={submitting}
        onComplete={complete}
        testidPrefix="dark-triad"
      />
    </PortalLayout>
  );
}
